import { apiFetch } from "./apiClient";

let currentWorkspaceId = null;

export const setKnowledgeWorkspaceId = (workspaceId) => {
  currentWorkspaceId = workspaceId || null;
};

const requireWorkspaceId = () => {
  if (!currentWorkspaceId) {
    throw new Error("Active workspace is not set");
  }
  return currentWorkspaceId;
};

const buildPath = (category, id) =>
  `${requireWorkspaceId()}/${category}/${id}`;

const uploadAsset = async ({ category, id, name, blob, mimeType }) => {
  const workspaceId = requireWorkspaceId();
  const payload = await apiFetch("/api/assets/signed-upload", {
    method: "POST",
    body: {
      workspace_id: workspaceId,
      category,
      id,
      name,
      mimeType: mimeType || blob?.type || "",
    },
  });
  const signedUrl = payload?.signedUrl || "";
  if (!signedUrl) {
    throw new Error("Signed upload URL missing");
  }
  const uploadResponse = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": mimeType || blob?.type || "application/octet-stream",
    },
    body: blob,
  });
  if (!uploadResponse.ok) {
    let responseText = "";
    try {
      responseText = await uploadResponse.text();
    } catch (error) {
      responseText = "";
    }
    const detail = responseText ? `: ${responseText.slice(0, 160)}` : "";
    throw new Error(
      `Failed to upload asset (${uploadResponse.status}${detail})`,
    );
  }

  const path = payload?.path || buildPath(category, id);
  let signedRead = null;
  try {
    signedRead = await apiFetch(
      `/api/assets/signed?path=${encodeURIComponent(path)}`,
    );
  } catch (error) {
    // Keep the stored path so URL refresh can recover later.
    signedRead = null;
  }
  return {
    id,
    name,
    mimeType: mimeType || blob?.type || "",
    path,
    url: signedRead?.url || "",
    signedAt: signedRead?.signedAt || new Date().toISOString(),
  };
};

const removeAsset = async ({ category, id }) => {
  const workspaceId = requireWorkspaceId();
  await apiFetch("/api/assets/delete", {
    method: "DELETE",
    body: { workspace_id: workspaceId, category, id },
  });
  return true;
};

// Upload an arbitrary generated image to a "generated" bucket category and
// return a shareable signed URL. Used by Video Studio for sharing with other AIs.
export const uploadGeneratedImage = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "generated", id, name, blob, mimeType });

// Assignment tab. Videos land here so the Modal transcription worker can pull
// them down from a signed URL instead of us shipping bytes through a lambda.
export const saveAssignmentAsset = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "assignment_ads", id, name, blob, mimeType });

export const deleteAssignmentAsset = async (id) =>
  removeAsset({ category: "assignment_ads", id });

export const saveKnowledgeImage = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "knowledge_images", id, name, blob, mimeType });

export const deleteKnowledgeImage = async (id) =>
  removeAsset({ category: "knowledge_images", id });

export const saveBrandAsset = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "brand_assets", id, name, blob, mimeType });

export const deleteBrandAsset = async (id) =>
  removeAsset({ category: "brand_assets", id });

export const saveAnalysisDoc = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "analysis_docs", id, name, blob, mimeType });

export const deleteAnalysisDoc = async (id) =>
  removeAsset({ category: "analysis_docs", id });

export const saveAdCopyDoc = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "ad_copy_docs", id, name, blob, mimeType });

export const deleteAdCopyDoc = async (id) =>
  removeAsset({ category: "ad_copy_docs", id });

export const saveBlogDoc = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "blog_docs", id, name, blob, mimeType });

export const deleteBlogDoc = async (id) =>
  removeAsset({ category: "blog_docs", id });

export const savePostExampleImage = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "post_examples", id, name, blob, mimeType });

export const deletePostExampleImage = async (id) =>
  removeAsset({ category: "post_examples", id });

export const savePostTypeDoc = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "post_type_docs", id, name, blob, mimeType });

export const deletePostTypeDoc = async (id) =>
  removeAsset({ category: "post_type_docs", id });

export const savePostCreativeAsset = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "post_creatives", id, name, blob, mimeType });

export const deletePostCreativeAsset = async (id) =>
  removeAsset({ category: "post_creatives", id });

export const saveActivityPreferenceAsset = async ({ id, name, blob, mimeType }) =>
  uploadAsset({ category: "activity_preferences", id, name, blob, mimeType });

export const deleteActivityPreferenceAsset = async (id) =>
  removeAsset({ category: "activity_preferences", id });

export const getKnowledgeImageStats = async () => ({ count: 0, bytes: 0 });
export const getBrandAssetStats = async () => ({ count: 0, bytes: 0 });
export const getAnalysisDocStats = async () => ({ count: 0, bytes: 0 });
export const getAdCopyDocStats = async () => ({ count: 0, bytes: 0 });
export const getBlogDocStats = async () => ({ count: 0, bytes: 0 });

export const getSignedAssetUrl = async (path) => {
  if (!path) return { url: "", signedAt: "" };
  try {
    const payload = await apiFetch(
      `/api/assets/signed?path=${encodeURIComponent(path)}`,
    );
    return {
      url: payload?.url || "",
      signedAt: payload?.signedAt || new Date().toISOString(),
    };
  } catch (error) {
    return { url: "", signedAt: "" };
  }
};

export const saveActivityPreference = async (record) => {
  await apiFetch("/api/activity-preferences", {
    method: "POST",
    body: { record },
  });
  return true;
};

export const getActivityPreferences = async () => {
  try {
    const payload = await apiFetch("/api/activity-preferences");
    return payload?.records || [];
  } catch (error) {
    return [];
  }
};

export const deleteActivityPreference = async (id) => {
  await apiFetch(`/api/activity-preferences?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  return true;
};
